import React, { useCallback, useEffect } from 'react'
import styled from '@emotion/styled'
import { useHistory, useParams } from 'react-router'
import AnimateHeightComp from 'react-animate-height'
import { toast } from 'react-toastify'
import { createSimpleStore } from 'react-simple-reducer'
import { Button, TituloPagina } from '../components'
import { Questao } from '../questoes/Questao'
import Svgs from '../svgs'
import {
  SimuladosStore,
  SimuladoDetalhes,
  ResultadoSimulado,
  getDetalhesByAvaliacaoId,
  getResultadoByAvaliacaoId,
  getStatusByAvaliacaoId,
} from './SimuladosStore'

const AnimateHeight: any = AnimateHeightComp

interface GabaritoSimuladoParams {
  cursoId: string
  avaliacaoId: string
}

interface QuestaoResumo {
  id: number
  textoQuestao: string
  percentualAcerto: number
}

interface GabaritoScreenData {
  avaliacaoId: string
  detalhes: SimuladoDetalhes | null
  resultado: ResultadoSimulado | null
  detalhesLoading: boolean
  detalhesErrorMessage: string
  resultadoLoading: boolean
  resultadoErrorMessage: string
  questoes: QuestaoResumo[]
  hasDetalhes: boolean
  hasQuestoes: boolean
}

const GabaritoStore = createSimpleStore(
  {
    avaliacaoId: '',
    questoesAbertasId: [] as number[],
    questoesJaAbertasId: [] as number[],
    detalhesErrorToastShown: false,
  },
  {
    setAvaliacaoId(state, avaliacaoId: string) {
      if (state.avaliacaoId === avaliacaoId) return
      state.avaliacaoId = avaliacaoId
      state.questoesAbertasId = []
      state.questoesJaAbertasId = []
      state.detalhesErrorToastShown = false
    },
    toggleQuestao(state, questaoId: number) {
      const estaAberta = state.questoesAbertasId.includes(questaoId)
      if (estaAberta) {
        state.questoesAbertasId = state.questoesAbertasId.filter((id) => id !== questaoId)
      } else {
        state.questoesAbertasId = [
          questaoId,
          ...state.questoesAbertasId.filter((id) => id !== questaoId),
        ]
      }

      if (!state.questoesJaAbertasId.includes(questaoId)) {
        state.questoesJaAbertasId = [...state.questoesJaAbertasId, questaoId]
      }
    },
    setDetalhesErrorToastShown(state, detalhesErrorToastShown: boolean) {
      state.detalhesErrorToastShown = detalhesErrorToastShown
    },
  }
)

export const GabaritoSimulado = () => {
  return (
    <GabaritoStore.Provider>
      <InitGabaritoScreen />
      <GabaritoSimuladoContent />
    </GabaritoStore.Provider>
  )
}

function InitGabaritoScreen() {
  const { avaliacaoId } = useParams<GabaritoSimuladoParams>()
  const dispatch = GabaritoStore.useDispatch()
  const simuladosDispatch = SimuladosStore.useDispatch()

  useEffect(() => {
    if (!avaliacaoId) return
    dispatch({ type: 'setAvaliacaoId', payload: avaliacaoId })
    simuladosDispatch(SimuladosStore.thunks.loadGabarito(avaliacaoId))
  }, [avaliacaoId, dispatch, simuladosDispatch])

  return null
}

function GabaritoSimuladoContent() {
  const screen = useGabaritoScreenData()

  return (
    <GabaritoSimulado.Layout>
      <HeaderGabarito />
      <ResumoGabarito screen={screen} />
      <MensagemErroGabarito screen={screen} />
      <EstadoGabarito screen={screen} />
      <ListaQuestoesGabarito screen={screen} />
    </GabaritoSimulado.Layout>
  )
}

function useGabaritoScreenData(): GabaritoScreenData {
  const { avaliacaoId: avaliacaoIdParam } = useParams<GabaritoSimuladoParams>()
  const { avaliacaoId } = GabaritoStore.useState()
  const avaliacaoIdResolvido = avaliacaoId || avaliacaoIdParam || ''
  const remote = useGabaritoRemoteState(avaliacaoIdResolvido)
  const screen = buildGabaritoScreenData({
    avaliacaoId: avaliacaoIdResolvido,
    ...remote,
  })

  useDetalhesErrorToast(screen.detalhesErrorMessage)

  return screen
}

function useGabaritoInteracaoState() {
  const { questoesAbertasId, questoesJaAbertasId } = GabaritoStore.useState()
  const dispatch = GabaritoStore.useDispatch()

  const toggleQuestao = useCallback(
    (questaoId: number) => {
      dispatch({ type: 'toggleQuestao', payload: questaoId })
    },
    [dispatch]
  )

  return {
    questoesAbertasId,
    questoesJaAbertasId,
    toggleQuestao,
  }
}

function useGabaritoRemoteState(avaliacaoId: string) {
  const {
    detalhesByAvaliacaoId,
    resultadoByAvaliacaoId,
    detalhesStatusByAvaliacaoId,
    resultadoStatusByAvaliacaoId,
  } = SimuladosStore.useState()

  const avaliacaoIdNumero = +avaliacaoId
  const detalhes = getDetalhesByAvaliacaoId(detalhesByAvaliacaoId, avaliacaoIdNumero) || null
  const resultado = getResultadoByAvaliacaoId(resultadoByAvaliacaoId, avaliacaoIdNumero) || null
  const detalhesStatus = getStatusByAvaliacaoId(detalhesStatusByAvaliacaoId, avaliacaoIdNumero)
  const resultadoStatus = getStatusByAvaliacaoId(resultadoStatusByAvaliacaoId, avaliacaoIdNumero)

  return {
    detalhes,
    resultado,
    detalhesStatus,
    resultadoStatus,
  }
}

function buildGabaritoScreenData({
  avaliacaoId,
  detalhes,
  resultado,
  detalhesStatus,
  resultadoStatus,
}: {
  avaliacaoId: string
  detalhes: SimuladoDetalhes | null
  resultado: ResultadoSimulado | null
  detalhesStatus: { loading: boolean; error: string }
  resultadoStatus: { loading: boolean; error: string }
}): GabaritoScreenData {
  const questoes = getQuestoes(detalhes || undefined)

  return {
    avaliacaoId,
    detalhes,
    resultado,
    detalhesLoading: detalhesStatus.loading,
    detalhesErrorMessage: detalhesStatus.error,
    resultadoLoading: resultadoStatus.loading,
    resultadoErrorMessage: resultadoStatus.error,
    questoes,
    hasDetalhes: !!detalhes,
    hasQuestoes: questoes.length > 0,
  }
}

function useDetalhesErrorToast(errorMessage: string) {
  const { detalhesErrorToastShown } = GabaritoStore.useState()
  const dispatch = GabaritoStore.useDispatch()

  useEffect(() => {
    if (!errorMessage) {
      if (detalhesErrorToastShown) {
        dispatch({ type: 'setDetalhesErrorToastShown', payload: false })
      }
      return
    }

    if (!detalhesErrorToastShown) {
      toast.error(errorMessage)
      dispatch({ type: 'setDetalhesErrorToastShown', payload: true })
    }
  }, [errorMessage, detalhesErrorToastShown, dispatch])
}

function getQuestoes(detalhes?: SimuladoDetalhes): QuestaoResumo[] {
  if (detalhes?.questoes?.length) return detalhes.questoes

  return (detalhes?.avaliacao?.avaliacoesQuestoes || []).map((item) => ({
    id: item.questaoId,
    textoQuestao: '',
    percentualAcerto: 0,
  }))
}

function getSubtituloModulo(modulosSelecionados: Array<{ id: number; nome: string }> = []) {
  if (modulosSelecionados.length === 1) return modulosSelecionados[0].nome
  if (modulosSelecionados.length > 1) return `Simulado com ${modulosSelecionados.length} módulos`
  return ''
}

function HeaderGabarito() {
  return (
    <GabaritoSimulado.Header>
      <GabaritoSimulado.VoltarButton />
    </GabaritoSimulado.Header>
  )
}

function ResumoGabarito({ screen }: { screen: GabaritoScreenData }) {
  const subtituloModulo = getSubtituloModulo(screen.detalhes?.modulosSelecionados || [])
  const mostrarTagsModulos = (screen.detalhes?.modulosSelecionados?.length || 0) > 1

  return (
    <>
      <GabaritoSimulado.TituloContainer>
        <TituloPagina>
          <GabaritoSimulado.TituloSub>Gabarito:</GabaritoSimulado.TituloSub>
          Simulado
        </TituloPagina>
        {subtituloModulo ? (
          <GabaritoSimulado.ModuloSubtitulo>{subtituloModulo}</GabaritoSimulado.ModuloSubtitulo>
        ) : null}
        {mostrarTagsModulos ? (
          <GabaritoSimulado.ModulosSelecionados>
            {screen.detalhes?.modulosSelecionados?.map((modulo) => (
              <GabaritoSimulado.ModuloTag key={modulo.id}>{modulo.nome}</GabaritoSimulado.ModuloTag>
            ))}
          </GabaritoSimulado.ModulosSelecionados>
        ) : null}
      </GabaritoSimulado.TituloContainer>

      {screen.resultado ? (
        <GabaritoSimulado.Aproveitamento>
          Você acertou <strong>{screen.resultado.qtdQuestoesCorretas}</strong> de{' '}
          <strong>{screen.resultado.qtdQuestoes}</strong> questões e teve um aproveitamento de{' '}
          <strong>{Math.ceil(screen.resultado.pontuacao || 0)}%</strong>
        </GabaritoSimulado.Aproveitamento>
      ) : null}

      {!screen.resultado && !screen.resultadoLoading && screen.resultadoErrorMessage ? (
        <GabaritoSimulado.ResultadoAviso>
          Não foi possível carregar o resumo de aproveitamento.
        </GabaritoSimulado.ResultadoAviso>
      ) : null}
    </>
  )
}

function MensagemErroGabarito({ screen }: { screen: GabaritoScreenData }) {
  if (!screen.detalhesErrorMessage) return null
  return <GabaritoSimulado.ErrorState>{screen.detalhesErrorMessage}</GabaritoSimulado.ErrorState>
}

function EstadoGabarito({ screen }: { screen: GabaritoScreenData }) {
  if (!screen.avaliacaoId) {
    return <GabaritoSimulado.LoadingState>Carregando gabarito...</GabaritoSimulado.LoadingState>
  }

  if (screen.detalhesLoading && !screen.hasDetalhes) {
    return <GabaritoSimulado.LoadingState>Carregando gabarito...</GabaritoSimulado.LoadingState>
  }

  if (screen.detalhesErrorMessage) return null

  if (screen.hasDetalhes && !screen.hasQuestoes) {
    return (
      <GabaritoSimulado.EmptyState>
        <div>
          <strong>Este simulado não possui questões para exibir.</strong>
        </div>
      </GabaritoSimulado.EmptyState>
    )
  }

  return null
}

const QuestaoCardGabarito = React.memo(
  ({
    questao,
    index,
    questaoAberta,
    questaoJaAberta,
    onToggle,
    avaliacaoId,
  }: {
    questao: QuestaoResumo
    index: number
    questaoAberta: boolean
    questaoJaAberta: boolean
    onToggle: (questaoId: number) => void
    avaliacaoId: string
  }) => {
    return (
      <div>
        <GabaritoSimulado.QuestaoResumoCard onClick={() => onToggle(questao.id)}>
          <GabaritoSimulado.Indicador percentualAcerto={questao.percentualAcerto}>
            {questao.percentualAcerto === 100 ? (
              <Svgs.Checkmark color="white" />
            ) : questao.percentualAcerto === 0 ? (
              <Svgs.Close color="white" height={16} width="auto" />
            ) : (
              Math.ceil(questao.percentualAcerto)
            )}
          </GabaritoSimulado.Indicador>

          <GabaritoSimulado.Enunciado>
            <strong>Questão {index + 1}</strong>
            {questao.textoQuestao ? (
              <div dangerouslySetInnerHTML={{ __html: questao.textoQuestao }} />
            ) : null}
          </GabaritoSimulado.Enunciado>

          <Svgs.ChevronLeft
            style={{ transform: questaoAberta ? 'rotate(-90deg)' : 'rotate(90deg)' }}
            color="#4D4D4D"
          />
        </GabaritoSimulado.QuestaoResumoCard>

        <AnimateHeight height={questaoAberta ? 'auto' : 0}>
          {questaoAberta || questaoJaAberta ? (
            <GabaritoSimulado.QuestaoContainer style={{ fontSize: 1 }}>
              <Questao
                questaoId={questao.id}
                somenteLeitura={true}
                isAvaliacao={false}
                isGabarito={true}
                avaliacaoId={avaliacaoId}
              />
            </GabaritoSimulado.QuestaoContainer>
          ) : null}
        </AnimateHeight>
      </div>
    )
  }
)

function ListaQuestoesGabarito({ screen }: { screen: GabaritoScreenData }) {
  const { questoesAbertasId, questoesJaAbertasId, toggleQuestao } = useGabaritoInteracaoState()

  if (!screen.hasDetalhes || !!screen.detalhesErrorMessage || !screen.hasQuestoes) return null

  return (
    <GabaritoSimulado.ListaQuestoes>
      {screen.questoes.map((questao, index) => (
        <QuestaoCardGabarito
          key={questao.id}
          questao={questao}
          index={index}
          questaoAberta={questoesAbertasId.includes(questao.id)}
          questaoJaAberta={questoesJaAbertasId.includes(questao.id)}
          onToggle={toggleQuestao}
          avaliacaoId={screen.avaliacaoId}
        />
      ))}
    </GabaritoSimulado.ListaQuestoes>
  )
}

GabaritoSimulado.Layout = styled.div`
  @media (max-width: 768px) {
    margin: 0 24px;
  }
  @media (max-width: 450px) {
    margin: 0 12px;
  }
`

GabaritoSimulado.Header = styled.div`
  margin-bottom: 18px;
`

const VoltarButtonBase = ({ className }: { className?: string }) => {
  const history = useHistory()
  const { cursoId } = useParams<GabaritoSimuladoParams>()

  function handleVoltarSimulados() {
    history.push(`/curso/${cursoId}/simulados`)
  }

  return (
    <Button
      className={className}
      onClick={handleVoltarSimulados}
      label="Voltar"
      svgPosition="LEFT"
      fill="CLEAR"
      svg={<Svgs.ChevronLeft color="#2D2D2D" />}
    />
  )
}

GabaritoSimulado.VoltarButton = styled(VoltarButtonBase)`
  justify-content: flex-end;
  width: auto;
  padding-right: 15px;
  .label {
    font-weight: bold;
  }
`

GabaritoSimulado.TituloContainer = styled.div`
  margin-bottom: 18px;
`

GabaritoSimulado.TituloSub = styled.span`
  font-weight: normal;
  font-size: 0.85em;
  margin-right: 6px;
`

GabaritoSimulado.ModuloSubtitulo = styled.div`
  margin-top: 14px;
  font-size: 14px;
  color: #2d2d2d;
  font-weight: 500;
`

GabaritoSimulado.ModulosSelecionados = styled.div`
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

GabaritoSimulado.ModuloTag = styled.span`
  background: #fff2ea;
  border: 1px solid #f06f30;
  border-radius: 999px;
  font-size: 13px;
  color: #f06f30;
  font-weight: 500;
  padding: 6px 12px;
`

GabaritoSimulado.Aproveitamento = styled.div`
  margin-bottom: 21px;
`

GabaritoSimulado.ResultadoAviso = styled.div`
  margin-bottom: 21px;
  color: #6a6a6a;
  font-size: 14px;
`

GabaritoSimulado.ListaQuestoes = styled.div`
  display: grid;
  gap: 8px;
`

GabaritoSimulado.QuestaoResumoCard = styled.div`
  background: #ffffff;
  border: 1px solid #d9d9d9;
  box-sizing: border-box;
  box-shadow: 0px 4px 16px rgba(51, 51, 51, 0.08);
  border-radius: 8px;
  margin-bottom: 8px;
  padding: 18px;
  display: grid;
  grid-gap: 16px;
  align-items: center;
  grid-template-columns: auto 1fr auto;
  cursor: pointer;
`

GabaritoSimulado.Enunciado = styled.div`
  display: grid;
  gap: 4px;
  max-height: 3.7em;
  overflow: hidden;
`

GabaritoSimulado.Indicador = styled.div<{ percentualAcerto: number }>((props) => {
  const cor =
    props.percentualAcerto === 100 ? '#27AE60' : props.percentualAcerto === 0 ? '#EB5757' : 'orange'

  return `
    background: ${cor};
    border-radius: 50%;
    height: 22px;
    width: 22px;
    display: grid;
    place-items: center;
    font-size: .8em;
    color: white;
    cursor: pointer;
  `
})

GabaritoSimulado.QuestaoContainer = styled.div`
  padding: 0 24px 24px 24px;
`

GabaritoSimulado.LoadingState = styled.div`
  text-align: center;
  color: #2d2d2d;
  font-size: 14px;
`

GabaritoSimulado.ErrorState = styled.div`
  color: #c43f2f;
  font-size: 14px;
  margin-bottom: 12px;
`

GabaritoSimulado.EmptyState = styled.div`
  font-size: 14px;
  line-height: 17px;
  color: #2d2d2d;
  text-align: center;
  display: grid;
  place-items: center;
  height: 40vh;
`
